/** Tour operator → Pre-check-in (`/tour-operators/:id/pre-check-in`, plan §3): the operator's
 *  pre-check-in form as a record page with a live dock.
 *
 *  - **Facts** on top: whether pre-check-in is on for the tours that inherit these settings,
 *    which tours they reach (links to the operator's tours), Edit `E` (or Discard / Done in
 *    edit mode); "not set up yet" when nothing is stored (saving creates it); a reason line
 *    and no Edit without `console_top_manage`.
 *  - **Sections** in the guest's reading order, one column (`PrecheckSections`); **the dock**
 *    (380 px, from 1100 px of content, in view mode too): **Preview | Changes N** with
 *    *Save N changes*, the required reason and ⌘S at its foot. Below 1100 px the ledger is
 *    the bottom sheet (edit mode) and Preview opens as a drawer from the facts row.
 *  - One draft (`operator_precheck`, sessionStorage-mirrored), one PUT with a reason
 *    (`useOperatorPrecheckApply`: 409 → refetch, Keep mine / Take theirs; 400 on rows).
 *  - **View first, `E` to edit** (`?edit=1`); ⌘S / ⌘Z / Esc are the ledger's. Leaving the
 *    tab with unsaved changes asks first (the tab owns the page's one blocker — the
 *    operator page mounts only the active tab).
 *  - States: loading skeletons (sections and dock), a failed GET with Retry, 403 / 404 as
 *    an empty state. */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Alert, Box, Button, Skeleton, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { getOperatorPreCheck, operatorPreCheckKey, type OperatorPreCheck } from '@/api/operatorPrecheck';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { LEAD_STEP, type GuestPreviewStep } from '@/features/precheck/preview/previewModel';
import { DraftProvider, useDraft, useDraftSummary, useDraftUnsavedGuard } from '@/features/records/draft/DraftProvider';
import { useRecordShortcuts } from '@/features/records/draft/useRecordShortcuts';
import { RecordEditorProvider, useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { DockSegments, useDockSegment } from '@/features/records/ledger/DockSegments';
import { Ledger, type LedgerVariant } from '@/features/records/ledger/Ledger';
import { LEDGER_GAP_PX, LedgerDock, SHEET_BELOW_PX } from '@/features/records/ledger/LedgerDock';
import { Kbd } from '@/features/records/page/Kbd';
import { RecordEmptyState } from '@/features/records/page/RecordStates';
import {
  APPBAR_PX,
  DOCK_GAP_PX,
  missingStatus,
  retryUnlessMissing,
  SCROLLER_PADDING_PX,
  useElementWidth,
} from '@/features/records/page/recordPage';
import { usePermissions } from '@/hooks/usePermissions';
import { formatZoned } from '@/utils/zonedTime';
import { toursHref } from '@/utils/gridLinks';

import { operatorPreCheckInHref } from '../tenantLinks';

import { useOperatorPreCheckBaseline } from './baselines';
import { OPERATOR_MANAGE_PERM } from './catalog';
import { OperatorPrecheckProvider, PreviewStepProvider, useOperatorPrecheck, useOperatorPrecheckData } from './context';
import { useOperatorPrecheckEditorConfig } from './editorConfig';
import { PrecheckPreviewDrawer, PrecheckPreviewPane } from './PrecheckPreview';
import { PrecheckSections } from './PrecheckSections';
import { OPERATOR_PRECHECK_WIDGETS } from './widgets';

/** The dock's width (plan §3): the tour record's. */
export const PRECHECK_DOCK_WIDTH = 380;

export function PreCheckInTab({ to }: { to: TourOperatorDetail }) {
  const { hasPerm } = usePermissions();
  const q = useQuery({
    queryKey: operatorPreCheckKey(to.id),
    queryFn: ({ signal }) => getOperatorPreCheck(to.id, signal),
    retry: retryUnlessMissing,
  });
  const missing = missingStatus(q.error);
  if (missing === 403) {
    return (
      <RecordEmptyState icon={<LockOutlinedIcon />} title="You can’t see this operator’s pre-check-in settings">
        Your console role does not include tour operators. Ask an Ops admin for the console_top_view permission.
      </RecordEmptyState>
    );
  }
  if (!q.data) {
    return q.error ? (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void q.refetch()}>
            Retry
          </Button>
        }
        data-precheck-load-error
      >
        Couldn’t load the pre-check-in settings{q.error instanceof Error && q.error.message ? ` — ${q.error.message}` : ''}.
      </Alert>
    ) : (
      <PrecheckSkeleton />
    );
  }
  return <PrecheckLoaded to={to} data={q.data} canEdit={hasPerm(OPERATOR_MANAGE_PERM)} />;
}

/** Loading: the facts row, the sections and the dock as they will stand. */
function PrecheckSkeleton() {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const docked = width === 0 || width >= SHEET_BELOW_PX;
  return (
    <Box ref={ref} role="status" aria-label="Loading the pre-check-in settings" data-precheck-skeleton>
      <Skeleton variant="text" width="45%" height={28} sx={{ mb: 2 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: docked ? `minmax(0, 1fr) ${PRECHECK_DOCK_WIDTH}px` : 'minmax(0, 1fr)', gap: `${LEDGER_GAP_PX}px` }}>
        <Box sx={{ display: 'grid', gap: 2, alignContent: 'start' }}>
          {[96, 120, 200, 200].map((h, i) => (
            <Skeleton key={i} variant="rounded" height={h} />
          ))}
        </Box>
        {docked && <Skeleton variant="rounded" height={480} />}
      </Box>
    </Box>
  );
}

function PrecheckLoaded({ to, data, canEdit }: { to: TourOperatorDetail; data: OperatorPreCheck; canEdit: boolean }) {
  const baseline = useOperatorPreCheckBaseline(data, to.id);
  const ctx = useOperatorPrecheckData({ topId: to.id, operatorName: to.name, tenantCode: to.code, baseline, canEdit });
  const config = useOperatorPrecheckEditorConfig({ baseline, canEdit, operatorName: to.name, widgets: OPERATOR_PRECHECK_WIDGETS });
  const [params, setParams] = useSearchParams();
  const editing = canEdit && params.get('edit') === '1';
  const setEdit = useCallback(
    (on: boolean) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          if (on) next.set('edit', '1');
          else next.delete('edit');
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const onExitEdit = useCallback(() => setEdit(false), [setEdit]);
  const [step, setStep] = useState<GuestPreviewStep>(LEAD_STEP);
  const stepValue = useMemo(() => ({ step, setStep }), [step]);

  return (
    <DraftProvider kind="operator_precheck" recordId={to.id} baselines={baseline.paths} baselineTs={null}>
      <RecordEditorProvider mode={editing ? 'edit' : 'view'} config={config} onExitEdit={onExitEdit}>
        <OperatorPrecheckProvider value={ctx}>
          <PreviewStepProvider value={stepValue}>
            <PrecheckShell topId={to.id} editing={editing} canEdit={canEdit} onEnterEdit={() => setEdit(true)} />
          </PreviewStepProvider>
        </OperatorPrecheckProvider>
      </RecordEditorProvider>
    </DraftProvider>
  );
}

function PrecheckShell({
  topId,
  editing,
  canEdit,
  onEnterEdit,
}: {
  topId: number;
  editing: boolean;
  canEdit: boolean;
  onEnterEdit: () => void;
}) {
  const editor = useRecordEditor();
  const { restored, discardRestored, dispatch } = useDraft();
  const { count } = useDraftSummary();
  const { prompt } = useDraftUnsavedGuard(operatorPreCheckInHref(topId), {
    message: 'You have unsaved changes to the pre-check-in settings. Leaving now will discard them.',
  });
  const [contentRef, contentWidth] = useElementWidth<HTMLDivElement>();
  // Below 1100 px of content there is no dock column: the Preview is a drawer.
  const narrow = contentWidth > 0 && contentWidth < SHEET_BELOW_PX;
  const [previewOpen, setPreviewOpen] = useState(false);
  // Closed by the page (widening past 1100 px: the dock takes over and gets the focus).
  const [autoClosed, setAutoClosed] = useState(false);
  useEffect(() => {
    if (previewOpen && !narrow) {
      setAutoClosed(true);
      setPreviewOpen(false);
    }
  }, [narrow, previewOpen]);
  const focusDock = useCallback(() => {
    setAutoClosed(false);
    const slot = document.querySelector<HTMLElement>(`[data-precheck-tab="${topId}"] [data-ledger-slot]`);
    const target = slot?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]') ?? slot?.querySelector<HTMLElement>('aside') ?? null;
    if (!target) return;
    if (target.tagName === 'ASIDE' && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus();
  }, [topId]);

  useRecordShortcuts(canEdit && !editing ? { onEdit: onEnterEdit } : {});

  return (
    <Box data-precheck-tab={topId} sx={{ minWidth: 0 }}>
      <FactsRow
        editing={editing}
        canEdit={canEdit}
        dirty={count > 0}
        onEdit={onEnterEdit}
        onDone={editor.requestClose}
        onDiscard={() => dispatch({ type: 'DISCARD_ALL' })}
        {...(narrow ? { onPreview: () => setPreviewOpen(true), previewOpen } : {})}
      />
      {!editing && count > 0 && (
        <Alert
          severity="info"
          icon={<EditNoteOutlinedIcon />}
          sx={{ mb: 2 }}
          data-view-draft-banner
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {canEdit && (
                <Button color="inherit" size="small" onClick={onEnterEdit}>
                  Continue editing
                </Button>
              )}
              <Button color="inherit" size="small" onClick={restored ? discardRestored : () => dispatch({ type: 'DISCARD_ALL' })}>
                Discard
              </Button>
            </Box>
          }
        >
          {restored
            ? `Restored ${count} unsaved ${count === 1 ? 'change' : 'changes'} from ${formatZoned(restored.savedAt, null, { weekday: false })} — not saved yet.`
            : `${count} unsaved ${count === 1 ? 'change is' : 'changes are'} kept in the editor — not saved yet.`}
        </Alert>
      )}
      <Box ref={contentRef}>
        <LedgerDock
          open={editing}
          dockInView
          width={PRECHECK_DOCK_WIDTH}
          stickyTop={DOCK_GAP_PX - SCROLLER_PADDING_PX}
          viewportOffset={APPBAR_PX + SCROLLER_PADDING_PX}
          ledger={(variant) => <PrecheckDock variant={variant} editing={editing} canEdit={canEdit} />}
        >
          <PrecheckSections />
        </LedgerDock>
      </Box>
      {/* Mounted while it may be open or closing — a close caused by widening still runs its
          exit and hands the focus to the dock. */}
      <PrecheckPreviewDrawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        restoreFocus={!autoClosed}
        {...(autoClosed ? { onExited: focusDock } : {})}
      />
      {prompt}
    </Box>
  );
}

/** The dock: Preview | Changes N over the ledger (its header, Save foot and shortcuts stay
 *  under both segments). The bottom sheet is the ledger alone — the Preview is a drawer. */
function PrecheckDock({ variant, editing, canEdit }: { variant: LedgerVariant; editing: boolean; canEdit: boolean }) {
  const { count } = useDraftSummary();
  const { receipt } = useRecordEditor();
  const { restored, state } = useDraft();
  const discarded = state.undoStack[state.undoStack.length - 1]?.kind === 'discard';
  const { segment, choose } = useDockSegment({ editing, count, receipt, restored: Boolean(restored) && count > 0, discarded });
  if (variant === 'sheet') return <Ledger variant={variant} restoredDiscard={false} />;
  return (
    <Ledger
      variant={variant}
      mode={editing ? 'edit' : 'view'}
      // By subject: the selected segment already says "Preview".
      viewTitle="Saved form"
      // The facts row's Discard and the ledger's Discard all are beside it.
      restoredDiscard={false}
      {...(canEdit ? { tabs: <DockSegments segment={segment} onChange={choose} editing={editing} count={count} /> } : {})}
      {...(segment === 'preview' ? { body: <PrecheckPreviewPane /> } : {})}
    />
  );
}

// ---------------------------------------------------------------------------
// Facts
// ---------------------------------------------------------------------------

function Fact({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <Typography variant="body2" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      {icon}
      {children}
    </Typography>
  );
}

const tours = (n: number) => `${n.toLocaleString('en')} ${n === 1 ? 'tour' : 'tours'}`;

function FactsRow({
  editing,
  canEdit,
  dirty,
  onEdit,
  onDone,
  onDiscard,
  onPreview,
  previewOpen = false,
}: {
  editing: boolean;
  canEdit: boolean;
  dirty: boolean;
  onEdit: () => void;
  onDone: () => void;
  onDiscard: () => void;
  /** Below 1100 px of content: open the preview's drawer. */
  onPreview?: () => void;
  previewOpen?: boolean;
}) {
  const { baseline, topId } = useOperatorPrecheck();
  const { stored, settings, reach } = baseline.data;
  const own = reach.own_settings_on + reach.own_settings_off;
  const toursLink = (n: number) => (
    <ValueActions actions={[act.filter(toursHref([], topId), 'Tours of this operator')]}>{tours(n)}</ValueActions>
  );
  const status = !stored ? (
    <Fact icon={<InfoOutlinedIcon aria-hidden sx={{ fontSize: 18, color: 'info.main' }} />}>
      <b>Not set up yet</b>&nbsp;— the defaults below; saving creates the settings
    </Fact>
  ) : settings.enabled ? (
    <Fact icon={<CheckCircleOutlineIcon aria-hidden sx={{ fontSize: 18, color: 'primary.main' }} />}>
      <b>On for tours that inherit</b>
    </Fact>
  ) : (
    <Fact icon={<RemoveCircleOutlineIcon aria-hidden sx={{ fontSize: 18, color: 'text.secondary' }} />}>
      <b>Off</b>
    </Fact>
  );

  return (
    <Box
      data-precheck-facts
      sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', minHeight: 44, mb: 2, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
        {status}
        <Typography variant="body2" color="text.secondary" component="span" data-precheck-reach sx={{ fontVariantNumeric: 'tabular-nums' }}>
          Used by {toursLink(reach.inheriting_tours)} · {toursLink(own)} {own === 1 ? 'has its' : 'have their'} own settings ({reach.own_settings_on.toLocaleString('en')} on)
        </Typography>
      </Box>
      {/* Right-aligned, also when a narrow row wraps it under the facts. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 'none', ml: 'auto', '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
        {onPreview && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<PhoneAndroidOutlinedIcon />}
            onClick={onPreview}
            aria-haspopup="dialog"
            aria-expanded={previewOpen}
            data-preview-open
          >
            Preview
          </Button>
        )}
        {!canEdit ? (
          <Typography variant="body2" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }} data-read-only>
            <LockOutlinedIcon aria-hidden sx={{ fontSize: 16 }} />
            Read-only — you lack the manage permission
          </Typography>
        ) : editing ? (
          <>
            <Tooltip title={dirty ? 'Discard every change — ⌘Z brings them back' : 'No changes to discard'}>
              <span>
                <Button size="small" color="error" onClick={onDiscard} disabled={!dirty} data-discard>
                  Discard
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" size="small" onClick={onDone} data-close-editor>
              Done
              <Kbd>Esc</Kbd>
            </Button>
          </>
        ) : (
          <Button variant="contained" size="small" startIcon={<EditOutlinedIcon />} onClick={onEdit} data-edit>
            Edit
            <Kbd>E</Kbd>
          </Button>
        )}
      </Box>
    </Box>
  );
}
